import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Input, Select, Table, Tag, Tooltip, Typography } from 'antd';
import {
  Alert,
  Button,
  Modal,
  Status,
} from '@insight-health-ai/roseate/components';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import { useMagicFlowContext } from '../MagicFlowContext';
import type { FlowEvent } from 'src/models';

type EventTableRecord = FlowEvent & { key: string };

type StatusAppearance = 'success' | 'error' | 'warning' | 'info' | 'neutral';

const EVENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'pending' },
  { value: 'processing', label: 'processing' },
  { value: 'processed', label: 'processed' },
  { value: 'failed', label: 'failed' },
  { value: 'no_match', label: 'no_match' },
  { value: 'skipped', label: 'skipped' },
];

const eventStatusAppearance = (status?: string): StatusAppearance => {
  if (!status) return 'neutral';
  const v = status.toLowerCase();
  if (v === 'processed' || v === 'success' || v.includes('complete'))
    return 'success';
  if (v === 'failed' || v === 'error') return 'error';
  if (v === 'processing' || v === 'pending') return 'warning';
  if (v === 'skipped' || v === 'no_match') return 'neutral';
  return 'info';
};

export const EventsTab = () => {
  const {
    hasOrgSelected,
    namespaceId,
    setNamespaceId,
    eventName,
    setEventName,
    eventId,
    setEventId,
    eventDataJson,
    setEventDataJson,
    namespaceOptions,
    isLoadingNamespaces,
    hasPublishErrors,
    isPublishingEvent,
    handlePublishEvent,
    events,
    eventsTotal,
    isLoadingEvents,
    isFetchingEvents,
    refreshEvents,
    eventsPage,
    setEventsPage,
    eventsPageSize,
    setEventsPageSize,
    eventsStatusFilter,
    setEventsStatusFilter,
    eventsNameFilter,
    setEventsNameFilter,
    eventsNamespaceFilter,
    setEventsNamespaceFilter,
  } = useMagicFlowContext();

  const [eventNameInput, setEventNameInput] = useState(eventsNameFilter);
  const [rawEventModal, setRawEventModal] = useState<{
    open: boolean;
    event: EventTableRecord | null;
  }>({
    open: false,
    event: null,
  });

  const tableData = useMemo<EventTableRecord[]>(
    () =>
      (events || []).map((event, index) => ({
        ...event,
        key: String(event.event_id || event.created_at || index),
      })),
    [events]
  );

  const columns = useMemo<ColumnsType<EventTableRecord>>(
    () => [
      {
        title: 'Event ID',
        dataIndex: 'event_id',
        width: 280,
        render: (value) => (
          <Typography.Text className="font-mono text-xs">
            {value || '-'}
          </Typography.Text>
        ),
      },
      {
        title: 'External ID',
        dataIndex: 'external_event_id',
        width: 220,
        render: (value: string | null | undefined) =>
          value ? (
            <Typography.Text className="font-mono text-xs">
              {value}
            </Typography.Text>
          ) : (
            '-'
          ),
      },
      {
        title: 'Event name',
        dataIndex: 'event_name',
        width: 220,
        render: (value: string) => (
          <Typography.Text className="font-mono text-xs">
            {value || '-'}
          </Typography.Text>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 130,
        render: (value: string | undefined, record) => (
          <Tooltip title={record.error || undefined}>
            <span>
              <Status
                appearance={eventStatusAppearance(value)}
                label={value || '-'}
              />
            </span>
          </Tooltip>
        ),
      },
      {
        title: 'Source',
        dataIndex: 'source',
        width: 90,
        render: (value: string | undefined) => value || '-',
      },
      {
        title: 'Matched flows',
        dataIndex: 'matched_flow_ids',
        width: 130,
        render: (value: string[] | undefined) => {
          const count = value?.length || 0;
          return (
            <Tooltip title={count ? value?.join('\n') : undefined}>
              <Tag color={count ? 'blue' : 'default'}>{count}</Tag>
            </Tooltip>
          );
        },
      },
      {
        title: 'Created runs',
        dataIndex: 'created_flow_run_ids',
        width: 130,
        render: (value: string[] | undefined) => {
          const count = value?.length || 0;
          return (
            <Tooltip title={count ? value?.join('\n') : undefined}>
              <Tag color={count ? 'green' : 'default'}>{count}</Tag>
            </Tooltip>
          );
        },
      },
      {
        title: 'Resumed runs',
        dataIndex: 'resumed_flow_run_ids',
        width: 130,
        render: (value: string[] | undefined) => {
          const count = value?.length || 0;
          return (
            <Tooltip title={count ? value?.join('\n') : undefined}>
              <Tag color={count ? 'cyan' : 'default'}>{count}</Tag>
            </Tooltip>
          );
        },
      },
      {
        title: 'Created',
        dataIndex: 'created_at',
        width: 200,
        render: (value: string | undefined) =>
          value ? dayjs(value).format('MMM D, YYYY h:mm:ss A') : '-',
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        render: (_, record) => (
          <Button
            variant="outlined"
            appearance="secondary"
            size="sm"
            onClick={() => setRawEventModal({ open: true, event: record })}
          >
            View Raw
          </Button>
        ),
      },
    ],
    []
  );

  const handleEventNameSubmit = () => {
    setEventsPage(1);
    setEventsNameFilter(eventNameInput.trim());
  };

  return (
    <div className="space-y-4">
      {!hasOrgSelected && (
        <Alert
          appearance="info"
          message="Select an organization to publish or browse events."
        />
      )}

      <div className="rounded-xl border border-gray-200 p-4 bg-white">
        <Typography.Title level={5} className="!mb-3">
          Publish Event
        </Typography.Title>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            placeholder="Select namespace"
            value={namespaceId || undefined}
            onChange={(value) => setNamespaceId(value)}
            options={namespaceOptions}
            loading={isLoadingNamespaces}
            disabled={!hasOrgSelected || isPublishingEvent}
            showSearch
            optionFilterProp="label"
          />
          <Input
            placeholder="event_name (e.g. appointment.scheduled)"
            value={eventName}
            onChange={(event) => setEventName(event.target.value)}
            disabled={!hasOrgSelected || isPublishingEvent}
          />
          <Input
            placeholder="event_id (idempotency key)"
            value={eventId}
            onChange={(event) => setEventId(event.target.value)}
            disabled={!hasOrgSelected || isPublishingEvent}
          />
        </div>
        <Input.TextArea
          className="mt-3 font-mono"
          autoSize={{ minRows: 6, maxRows: 12 }}
          placeholder='{"key":"value"}'
          value={eventDataJson}
          onChange={(event) => setEventDataJson(event.target.value)}
          disabled={!hasOrgSelected || isPublishingEvent}
        />
        <div className="mt-3 flex justify-end">
          <Button
            appearance="primary"
            onClick={handlePublishEvent}
            disabled={!hasOrgSelected || hasPublishErrors || isPublishingEvent}
            loading={isPublishingEvent}
          >
            Publish Event
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 p-4 space-y-3 bg-white">
        <Typography.Title level={5} className="!mb-0">
          Filters
        </Typography.Title>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Filter by namespace"
            value={eventsNamespaceFilter || undefined}
            onChange={(value) => {
              setEventsPage(1);
              setEventsNamespaceFilter(value || '');
            }}
            options={namespaceOptions}
            loading={isLoadingNamespaces}
            disabled={!hasOrgSelected}
          />
          <Select
            allowClear
            showSearch
            placeholder="Filter by status"
            value={eventsStatusFilter || undefined}
            onChange={(value) => {
              setEventsPage(1);
              setEventsStatusFilter(value || '');
            }}
            options={EVENT_STATUS_OPTIONS}
            disabled={!hasOrgSelected}
          />
          <Input
            allowClear
            placeholder="Filter by event_name"
            prefix={<SearchOutlined />}
            value={eventNameInput}
            onChange={(e) => setEventNameInput(e.target.value)}
            onPressEnter={handleEventNameSubmit}
            onBlur={handleEventNameSubmit}
            disabled={!hasOrgSelected}
          />
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <Typography.Title level={5} className="!mb-0">
            Events ({eventsTotal})
          </Typography.Title>
          <Button
            variant="outlined"
            appearance="secondary"
            size="sm"
            icon={<ReloadOutlined />}
            onClick={refreshEvents}
            disabled={!hasOrgSelected || isLoadingEvents}
            loading={isFetchingEvents}
          >
            Refresh
          </Button>
        </div>
        <div className="rounded-xl shadow-xl bg-white">
          <Table
            className="appointment-page-table"
            dataSource={tableData}
            columns={columns}
            rowKey="key"
            loading={isLoadingEvents || isFetchingEvents}
            scroll={{ x: 1700 }}
            pagination={{
              current: eventsPage,
              pageSize: eventsPageSize,
              total: eventsTotal,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (newPage, newSize) => {
                setEventsPage(newPage);
                if (newSize && newSize !== eventsPageSize) {
                  setEventsPageSize(newSize);
                }
              },
              showTotal: (total) => `${total} events`,
            }}
            locale={{
              emptyText: hasOrgSelected
                ? 'No events'
                : 'Select an organization',
            }}
          />
        </div>
      </div>

      <Modal
        open={rawEventModal.open}
        onClose={() => setRawEventModal({ open: false, event: null })}
        title="Event Raw Data"
        width={900}
      >
        <pre className="m-0 max-h-[60vh] overflow-auto rounded bg-gray-50 p-3 text-xs">
          {JSON.stringify(rawEventModal.event, null, 2)}
        </pre>
      </Modal>
    </div>
  );
};
